<template>
  <div class="role-management-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>角色管理</span>
      </div>
    </div>
    
    <!-- 搜索区域 -->
    <div class="search-area">
      <el-input
        v-model="searchParams.roleName"
        placeholder="角色名称"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      <el-input
        v-model="searchParams.description"
        placeholder="角色描述"
        clearable
        class="search-input"
        prefix-icon="el-icon-search"
      ></el-input>
      <el-button type="primary" @click="handleSearch" icon="el-icon-search">搜索</el-button>
      <el-button
        v-permission="'system:role-management:create'"
        type="primary"
        @click="handleCreate"
        icon="el-icon-plus"
        style="margin-left: 10px;"
      >新增角色</el-button>
    </div>
    
    <!-- 表格区域 -->
    <el-table
      :data="roleList"
      border
      style="width: 100%"
      v-loading="loading"
      :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
    >
      <el-table-column prop="roleName" label="角色名称" min-width="150" align="center"></el-table-column>
      <el-table-column prop="description" label="角色描述" min-width="200" align="center"></el-table-column>
      <el-table-column label="用户数量" width="100" align="center">
        <template slot-scope="scope">
          <el-tag type="info" size="small">{{ scope.row.userCount || 0 }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="权限数量" width="100" align="center">
        <template slot-scope="scope">
          <el-tag type="success" size="small">{{ scope.row.menuCount || 0 }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="创建人" width="120" align="center">
        <template slot-scope="scope">
          {{ scope.row.creatorName || '-' }}
        </template>
      </el-table-column>
      <el-table-column label="创建时间" width="180" align="center">
        <template slot-scope="scope">
          {{ formatDateTime(scope.row.createTime) }}
        </template>
      </el-table-column>
      <el-table-column label="操作" width="280" align="center" fixed="right">
        <template slot-scope="scope">
          <el-button
            v-permission="'system:role-management:assign-permissions'"
            type="primary"
            size="mini"
            @click="handleAssignPermissions(scope.row)"
          >分配权限</el-button>
          <el-button
            v-permission="'system:role-management:edit'"
            type="primary"
            size="mini"
            @click="handleEdit(scope.row)"
          >编辑</el-button>
          <el-button
            v-permission="'system:role-management:delete'"
            type="danger"
            size="mini"
            @click="handleDelete(scope.row)"
          >删除</el-button>
        </template>
      </el-table-column>
    </el-table>
    
    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
        :current-page="pagination.currentPage"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="pagination.pageSize"
        :total="pagination.total"
        layout="total, sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>
    
    <!-- 新增/编辑对话框 -->
    <el-dialog
      :title="dialogStatus === 'add' ? '新增角色' : '编辑角色'"
      :visible.sync="dialogVisible"
      width="600px"
      :close-on-click-modal="false"
    >
      <el-form
        :model="roleForm"
        :rules="rules"
        ref="roleForm"
        label-width="100px"
      >
        <el-form-item label="角色名称" prop="roleName">
          <el-input v-model="roleForm.roleName" placeholder="请输入角色名称"></el-input>
        </el-form-item>
        <el-form-item label="角色描述" prop="description">
          <el-input
            v-model="roleForm.description"
            type="textarea"
            :rows="3"
            placeholder="请输入角色描述"
          ></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitForm">保存</el-button>
      </div>
    </el-dialog>
    
    <!-- 分配权限对话框 -->
    <el-dialog
      title="分配权限"
      :visible.sync="permissionDialogVisible"
      width="760px"
      top="5vh"
      custom-class="permission-dialog"
      :close-on-click-modal="false"
    >
      <div class="permission-dialog-content">
        <div class="permission-toolbar">
          <el-input
            v-model="permissionFilterText"
            size="small"
            clearable
            prefix-icon="el-icon-search"
            placeholder="搜索权限名称"
            class="permission-filter"
          ></el-input>
          <div class="permission-toolbar-actions">
            <el-button size="small" icon="el-icon-check" @click="selectAllPermissions">全选</el-button>
            <el-button size="small" icon="el-icon-close" @click="clearAllPermissions">取消全选</el-button>
          </div>
        </div>
        <div class="permission-tree-scroll">
          <el-tree
            ref="permissionTree"
            :data="filteredPermissionTree"
            :props="treeProps"
            :filter-node-method="filterPermissionNode"
            show-checkbox
            node-key="id"
            :default-expand-all="true"
            check-strictly
            @check="handleNodeCheck"
          ></el-tree>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="permissionDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitPermissions">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  query_roles_pagination,
  create_role,
  update_role,
  delete_role,
  assign_permissions_to_role,
  check_role_name,
  get_role_by_id
} from '@/api/modules/role'
import { get_permission_tree, get_role_permission_tree } from '@/api/modules/permission'
import moment from 'moment'

export default {
  name: 'RoleManagement',
  data() {
    return {
      loading: false,
      roleList: [],
      searchParams: {
        roleName: '',
        description: ''
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        total: 0
      },
      dialogVisible: false,
      dialogStatus: 'add', // 'add' 或 'edit'
      roleForm: {
        id: null,
        roleName: '',
        description: ''
      },
      originalRoleName: '', // 保存编辑前的原始角色名称
      rules: {
        roleName: [
          { required: true, message: '请输入角色名称', trigger: 'blur' },
          { max: 50, message: '角色名称长度不能超过50个字符', trigger: 'blur' },
          { validator: this.validateRoleName, trigger: 'blur' }
        ],
        description: [
          { max: 200, message: '角色描述长度不能超过200个字符', trigger: 'blur' }
        ]
      },
      permissionDialogVisible: false,
      currentRoleId: null,
      permissionTree: [],
      filteredPermissionTree: [], // 过滤后的权限树（只显示顶层菜单）
      permissionFilterText: '',
      treeProps: {
        children: 'children',
        label: 'permissionName'
      },
      checkedPermissionIds: []
    }
  },
  created() {
    this.fetchRoleList()
  },
  watch: {
    permissionFilterText(value) {
      if (this.$refs.permissionTree) {
        this.$refs.permissionTree.filter(value)
      }
    }
  },
  methods: {
    // 获取角色列表
    async fetchRoleList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage - 1,
          size: this.pagination.pageSize,
          sortBy: 'id',
          sortDir: 'desc'
        }
        
        if (this.searchParams.roleName) {
          params.roleName = this.searchParams.roleName
        }
        if (this.searchParams.description) {
          params.description = this.searchParams.description
        }
        
        const response = await query_roles_pagination(params)
        
        if (response.data && response.data.success) {
          const data = response.data.data
          this.roleList = data.content || []
          this.pagination.total = data.totalElements || 0
          this.pagination.currentPage = (data.number || 0) + 1
        } else {
          this.$message.error(response.data.message || '查询失败')
        }
      } catch (error) {
        console.error('查询角色列表失败:', error)
        this.$message.error('查询失败: ' + (error.message || '未知错误'))
      } finally {
        this.loading = false
      }
    },
    
    // 搜索
    handleSearch() {
      this.pagination.currentPage = 1
      this.fetchRoleList()
    },
    
    // 新增角色
    handleCreate() {
      this.dialogStatus = 'add'
      this.roleForm = {
        id: null,
        roleName: '',
        description: ''
      }
      this.originalRoleName = '' // 重置原始角色名称
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.roleForm) {
          this.$refs.roleForm.clearValidate()
        }
      })
    },
    
    // 编辑角色
    handleEdit(row) {
      this.dialogStatus = 'edit'
      this.roleForm = {
        id: row.id,
        roleName: row.roleName,
        description: row.description || ''
      }
      this.originalRoleName = row.roleName // 保存原始角色名称
      this.dialogVisible = true
      this.$nextTick(() => {
        if (this.$refs.roleForm) {
          this.$refs.roleForm.clearValidate()
        }
      })
    },
    
    // 删除角色
    async handleDelete(row) {
      // 先获取角色详情，检查是否有用户使用
      try {
        const detailResponse = await get_role_by_id(row.id)
        if (detailResponse.data && detailResponse.data.success) {
          const roleDetail = detailResponse.data.data
          const userCount = roleDetail.userCount || 0
          
          if (userCount > 0) {
            this.$confirm(
              `该角色下还有 ${userCount} 个用户，无法删除。请先移除该角色下的所有用户后再删除。`,
              '提示',
              {
                confirmButtonText: '确定',
                showCancelButton: false,
                type: 'warning'
              }
            )
            return
          }
        }
      } catch (error) {
        console.error('获取角色详情失败:', error)
      }
      
      // 确认删除
      this.$confirm('确认删除该角色?', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const response = await delete_role(row.id)
          if (response.data && response.data.success) {
            this.$message.success('删除成功')
            this.fetchRoleList()
          } else {
            this.$message.error(response.data.message || '删除失败')
          }
        } catch (error) {
          console.error('删除角色失败:', error)
          const errorMessage = error.response?.data?.message || error.message || '未知错误'
          this.$message.error('删除失败: ' + errorMessage)
        }
      }).catch(() => {})
    },
    
    // 分配权限
    async handleAssignPermissions(row) {
      this.currentRoleId = row.id
      this.permissionFilterText = ''
      this.permissionDialogVisible = true
      
      try {
        // 获取完整权限树
        const treeResponse = await get_permission_tree()
        if (treeResponse.data && treeResponse.data.success) {
          this.permissionTree = treeResponse.data.data || []
          // 过滤权限树：只显示顶层菜单权限（parentId为null的MENU类型），按钮权限作为子节点显示
          this.filteredPermissionTree = this.filterPermissionTree(this.permissionTree)
        }
        
        // 获取角色已有权限树（根据API文档使用 /permissions/role/{roleId}/tree）
        const rolePermResponse = await get_role_permission_tree(row.id)
        
        // 等待DOM更新和树组件渲染完成
        await this.$nextTick()
        await new Promise(resolve => setTimeout(resolve, 200))
        
        if (rolePermResponse.data && rolePermResponse.data.success) {
          const rolePermissionTree = rolePermResponse.data.data || []
          
          // 从角色权限树中提取所有节点的ID（包括父节点和子节点）
          const roleAllNodeIds = this.extractPermissionIds(rolePermissionTree)
          
          // 构建角色权限树中所有节点ID的Set（这是唯一真实来源）
          const roleNodeIdSet = new Set(roleAllNodeIds)
          
          // 从完整权限树中提取所有节点ID
          const allNodeIds = this.extractPermissionIds(this.permissionTree)
          
          // 严格过滤：只选中那些在角色权限树中真实存在的节点ID
          // 如果节点不在角色权限树中，即使它的父节点被选中，也不应该被选中
          this.checkedPermissionIds = allNodeIds.filter(id => roleNodeIdSet.has(id))
        } else {
          this.checkedPermissionIds = []
        }
        
        // 等待DOM更新后再设置选中状态
        await this.$nextTick()
        if (this.$refs.permissionTree) {
          // 先清空所有选中状态
          this.$refs.permissionTree.setCheckedKeys([])
          await this.$nextTick()
          // 再设置正确的选中状态（check-strictly已设置，不会级联选中）
          if (this.checkedPermissionIds.length > 0) {
            this.$refs.permissionTree.setCheckedKeys(this.checkedPermissionIds)
          }
        }
      } catch (error) {
        console.error('获取权限数据失败:', error)
        this.$message.error('获取权限数据失败: ' + (error.message || '未知错误'))
      }
    },

    filterPermissionNode(value, data) {
      if (!value) return true
      return (data.permissionName || '').toLowerCase().includes(value.trim().toLowerCase())
    },
    
    // 过滤权限树：只显示顶层菜单权限（parentId为null的MENU类型），按钮权限作为子节点显示
    filterPermissionTree(permissionTree) {
      if (!permissionTree || !Array.isArray(permissionTree)) {
        return []
      }
      
      // 第一步：扁平化所有权限，收集到一个数组中
      const allPermissions = []
      const collectPermissions = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) {
          return
        }
        nodes.forEach(node => {
          if (node && node.id) {
            allPermissions.push({
              ...node,
              // 移除children，稍后根据parentId重新构建
              children: undefined
            })
          }
          if (node.children && node.children.length > 0) {
            collectPermissions(node.children)
          }
        })
      }
      collectPermissions(permissionTree)
      
      // 第二步：只保留顶层菜单权限（parentId为null或undefined的MENU类型）
      // 按钮权限（BUTTON）和API权限（API）不应该作为顶层节点
      const topLevelMenus = allPermissions
        .filter(perm => {
          const isMenu = perm.permissionType === 'MENU'
          const isTopLevel = perm.parentId === null || perm.parentId === undefined
          return isMenu && isTopLevel
        })
        .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
        .map(perm => this.buildPermissionNode(perm, allPermissions))
      
      return topLevelMenus
    },
    
    // 构建权限节点（递归构建完整的树结构）
    buildPermissionNode(permission, allPermissions) {
      // 查找该权限的所有子权限（通过parentId匹配）
      // 需要处理parentId可能是数字或字符串的情况
      const children = allPermissions
        .filter(perm => {
          // 确保parentId匹配（处理类型转换）
          const permParentId = perm.parentId
          const currentId = permission.id
          // 同时支持数字和字符串比较
          return permParentId === currentId || 
                 permParentId === String(currentId) || 
                 Number(permParentId) === Number(currentId)
        })
        .sort((a, b) => {
          // 先按类型排序：MENU在前，BUTTON在中，API在后
          const typeOrder = { MENU: 1, BUTTON: 2, API: 3 }
          const typeDiff = (typeOrder[a.permissionType] || 999) - (typeOrder[b.permissionType] || 999)
          if (typeDiff !== 0) {
            return typeDiff
          }
          // 同类型按sortOrder排序
          return (a.sortOrder || 0) - (b.sortOrder || 0)
        })
        .map(child => this.buildPermissionNode(child, allPermissions))
      
      return {
        ...permission,
        children: children.length > 0 ? children : undefined
      }
    },
    
    
    // 从权限树中提取所有权限ID（递归提取）
    extractPermissionIds(permissionTree) {
      const ids = []
      const extract = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) {
          return
        }
        nodes.forEach(node => {
          if (node.id) {
            ids.push(node.id)
          }
          if (node.children && node.children.length > 0) {
            extract(node.children)
          }
        })
      }
      extract(permissionTree)
      return ids
    },
    
    // 从权限树中只提取叶子节点（最后一层，没有子节点的节点）的ID
    extractLeafNodeIds(permissionTree) {
      const ids = []
      const extract = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) {
          return
        }
        nodes.forEach(node => {
          // 如果是叶子节点（没有子节点或子节点为空），则提取其ID
          if (node.id && (!node.children || node.children.length === 0)) {
            ids.push(node.id)
          }
          // 如果有子节点，继续递归
          if (node.children && node.children.length > 0) {
            extract(node.children)
          }
        })
      }
      extract(permissionTree)
      return ids
    },
    
    // 只设置叶子节点的选中状态（不考虑父节点）
    setLeafNodesChecked(treeRef, treeData, checkedLeafIds) {
      if (!treeRef || !treeData || !Array.isArray(treeData)) {
        return
      }
      
      const checkedSet = new Set(checkedLeafIds)
      
      // 递归遍历树，只设置叶子节点的选中状态
      const setLeafChecked = (nodes) => {
        if (!nodes || !Array.isArray(nodes)) {
          return
        }
        nodes.forEach(node => {
          // 如果是叶子节点且在选中列表中，则选中它
          if (node.id && (!node.children || node.children.length === 0)) {
            if (checkedSet.has(node.id)) {
              // 只设置这个叶子节点，不级联
              treeRef.setChecked(node.id, true, false) // 第三个参数false表示不级联选中子节点
            } else {
              // 确保不在选中列表中的叶子节点不被选中
              treeRef.setChecked(node.id, false, false)
            }
          }
          // 递归处理子节点
          if (node.children && node.children.length > 0) {
            setLeafChecked(node.children)
          }
        })
      }
      
      setLeafChecked(treeData)
    },
    
    // 全选权限（选中所有节点，包括父节点和子节点）
    selectAllPermissions() {
      if (!this.$refs.permissionTree || !this.filteredPermissionTree) {
        return
      }
      
      // 从权限树中提取所有节点ID（包括所有父节点和子节点）
      const allNodeIds = this.extractPermissionIds(this.filteredPermissionTree)
      
      // 设置所有节点为选中状态
      if (allNodeIds.length > 0) {
        this.$refs.permissionTree.setCheckedKeys(allNodeIds)
      }
    },
    
    // 取消全选权限
    clearAllPermissions() {
      if (!this.$refs.permissionTree) {
        return
      }
      
      // 清空所有选中状态
      this.$refs.permissionTree.setCheckedKeys([])
    },
    
    // 处理节点选中状态变化（级联选中/取消子节点）
    handleNodeCheck(data, checkedInfo) {
      if (!this.$refs.permissionTree) {
        return
      }
      
      const nodeId = data.id
      const checkedKeys = checkedInfo.checkedKeys || []
      const isChecked = checkedKeys.includes(nodeId)
      
      // 如果节点被选中，递归选中所有子节点
      if (isChecked) {
        this.selectChildrenNodes(data)
      } else {
        // 如果节点被取消选中，递归取消所有子节点的选中
        this.unselectChildrenNodes(data)
      }
    },
    
    // 递归选中节点的所有子节点
    selectChildrenNodes(node) {
      if (!this.$refs.permissionTree || !node) {
        return
      }
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          // 选中子节点（第三个参数为false，因为我们使用的是check-strictly，不会自动级联）
          this.$refs.permissionTree.setChecked(child.id, true, false)
          // 递归处理子节点的子节点
          this.selectChildrenNodes(child)
        })
      }
    },
    
    // 递归取消节点的所有子节点的选中
    unselectChildrenNodes(node) {
      if (!this.$refs.permissionTree || !node) {
        return
      }
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          // 取消选中子节点（第三个参数为false，因为我们使用的是check-strictly，不会自动级联）
          this.$refs.permissionTree.setChecked(child.id, false, false)
          // 递归处理子节点的子节点
          this.unselectChildrenNodes(child)
        })
      }
    },
    
    // 提交权限分配
    async submitPermissions() {
      try {
        const checkedKeys = this.$refs.permissionTree.getCheckedKeys()
        const halfCheckedKeys = this.$refs.permissionTree.getHalfCheckedKeys()
        const allCheckedKeys = [...checkedKeys, ...halfCheckedKeys]
        
        const response = await assign_permissions_to_role({
          roleId: this.currentRoleId,
          permissionIds: allCheckedKeys
        })
        
        if (response.data && response.data.success) {
          this.$message.success('权限分配成功')
          this.permissionDialogVisible = false
          this.fetchRoleList() // 重新加载角色列表以更新权限数量
        } else {
          this.$message.error(response.data.message || '权限分配失败')
        }
      } catch (error) {
        console.error('分配权限失败:', error)
        this.$message.error('分配权限失败: ' + (error.response?.data?.message || error.message || '未知错误'))
      }
    },
    
    // 验证角色名称
    async validateRoleName(rule, value, callback) {
      if (!value) {
        callback()
        return
      }
      
      // 编辑时，如果角色名称没有变化，不需要检查
      if (this.dialogStatus === 'edit' && this.originalRoleName === value) {
        callback()
        return
      }
      
      // 检查角色名称是否已存在
      try {
        const response = await check_role_name(value)
        if (response.data && response.data.success) {
          const exists = response.data.data
          if (exists) {
            callback(new Error('角色名称已存在'))
          } else {
            callback()
          }
        } else {
          callback()
        }
      } catch (error) {
        console.error('检查角色名称失败:', error)
        // 检查失败时不阻止提交，让后端验证
        callback()
      }
    },
    
    // 提交表单
    async submitForm() {
      try {
        await this.$refs.roleForm.validate()
        
        if (this.dialogStatus === 'add') {
          const response = await create_role(this.roleForm)
          if (response.data && response.data.success) {
            this.$message.success('创建成功')
            this.dialogVisible = false
            this.fetchRoleList()
          } else {
            this.$message.error(response.data.message || '创建失败')
          }
        } else {
          const response = await update_role(this.roleForm.id, this.roleForm)
          if (response.data && response.data.success) {
            this.$message.success('更新成功')
            this.dialogVisible = false
            this.fetchRoleList()
          } else {
            this.$message.error(response.data.message || '更新失败')
          }
        }
      } catch (error) {
        if (error.message) {
          this.$message.error(error.message)
        }
      }
    },
    
    // 分页大小改变
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
      this.fetchRoleList()
    },
    
    // 当前页改变
    handleCurrentChange(val) {
      this.pagination.currentPage = val
      this.fetchRoleList()
    },
    
    // 格式化日期时间
    formatDateTime(date) {
      return date ? moment(date).format('YYYY-MM-DD HH:mm:ss') : '-'
    }
  }
}
</script>

<style scoped>
.role-management-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.header-title {
  display: flex;
  align-items: center;
  font-size: 18px;
  font-weight: bold;
}

.title-mark {
  width: 4px;
  height: 18px;
  background: #409EFF;
  margin-right: 10px;
}

.search-area {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
}

.search-input {
  width: 200px;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

.permission-dialog-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.permission-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 20px;
  border-bottom: 1px solid #ebeef5;
  background: #f8f9fb;
}

.permission-filter {
  width: 280px;
}

.permission-toolbar-actions {
  display: flex;
  flex-shrink: 0;
}

.permission-tree-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 20px 18px;
}

::v-deep .permission-dialog {
  display: flex;
  flex-direction: column;
  width: min(760px, calc(100vw - 32px)) !important;
  height: min(760px, 90vh);
  max-height: 90vh;
  margin-bottom: 0;
  overflow: hidden;
}

::v-deep .permission-dialog .el-dialog__header {
  flex-shrink: 0;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .permission-dialog .el-dialog__body {
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
}

::v-deep .permission-dialog .el-dialog__footer {
  flex-shrink: 0;
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  background: #fff;
}

::v-deep .permission-tree-scroll .el-tree-node__content {
  min-height: 30px;
  height: auto;
  padding: 3px 0;
}

@media (max-width: 600px) {
  .permission-toolbar {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }

  .permission-filter {
    width: 100%;
  }

  .permission-toolbar-actions {
    justify-content: flex-end;
  }
}
</style>
